import { Link, Outlet, createRootRoute, useRouterState } from '@tanstack/react-router';
import { Suspense } from 'react';
import { LoadingState, cn } from '@cca/ui';
import {
  BarChart3,
  Bot,
  CheckSquare,
  Inbox,
  LayoutDashboard,
  LogOut,
  Mail,
  ScrollText,
  Settings,
  ShieldAlert,
  ShoppingCart,
  Users,
  Workflow,
} from 'lucide-react';
import { RuntimeConfigProvider, useRuntimeConfig } from '../lib/runtime-config.js';
import { SessionProvider, useSession } from '../lib/session.js';
import { SendingDisabledNotice, SimulatorBanner } from '../components/simulator-banner.js';

/**
 * Navigation mirrors the operational sections in the product spec, but an entry
 * appears here only once its route is backed by real data. A link to an empty
 * page is a promise the build has not kept, so sections arrive with their
 * milestone rather than sitting in the nav doing nothing.
 */
const NAVIGATION = [
  { to: '/', label: 'Overview', icon: LayoutDashboard },
  { to: '/agent', label: 'AI Agent', icon: Bot },
  { to: '/inbox', label: 'Inbox', icon: Inbox },
  { to: '/orders', label: 'Orders', icon: ShoppingCart },
  { to: '/exceptions', label: 'Order Exceptions', icon: ShieldAlert },
  { to: '/briefs', label: 'Weekly Briefs', icon: Mail },
  { to: '/campaigns', label: 'Campaigns', icon: BarChart3 },
  { to: '/contacts', label: 'Contacts', icon: Users },
  { to: '/approvals', label: 'Approvals', icon: CheckSquare },
  { to: '/automations', label: 'Automations', icon: Workflow },
  { to: '/audit', label: 'Audit Log', icon: ScrollText },
  { to: '/settings', label: 'Settings', icon: Settings },
] as const;

function Sidebar() {
  const config = useRuntimeConfig();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  return (
    <nav
      aria-label="Main"
      className="flex w-60 shrink-0 flex-col border-r border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900"
    >
      <div className="border-b border-slate-200 px-4 py-4 dark:border-slate-800">
        <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
          {config.product.name}
        </p>
        <p className="text-xs text-slate-500 dark:text-slate-400">{config.product.agentName}</p>
      </div>
      <ul className="flex-1 space-y-0.5 overflow-y-auto p-2">
        {NAVIGATION.map(({ to, label, icon: Icon }) => {
          const active = to === '/' ? pathname === '/' : pathname.startsWith(to);
          return (
            <li key={to}>
              <Link
                to={to}
                aria-current={active ? 'page' : undefined}
                className={cn(
                  'flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors',
                  'focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:outline-none',
                  active
                    ? 'bg-sky-50 font-medium text-sky-800 dark:bg-sky-950 dark:text-sky-200'
                    : 'text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800',
                )}
              >
                <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
                {label}
              </Link>
            </li>
          );
        })}
      </ul>
      <SidebarFooter environment={config.appEnv} />
    </nav>
  );
}

function SidebarFooter({ environment }: { readonly environment: string }) {
  const { client, session } = useSession();
  const email = session?.user.email ?? null;

  return (
    <div className="space-y-2 border-t border-slate-200 px-4 py-3 dark:border-slate-800">
      {email ? (
        <>
          <p className="truncate text-xs text-slate-600 dark:text-slate-300" title={email}>
            {email}
          </p>
          <button
            type="button"
            onClick={() => void client?.auth.signOut()}
            className="flex items-center gap-1.5 rounded-md text-xs text-slate-500 hover:text-slate-900 focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:outline-none dark:text-slate-400 dark:hover:text-slate-100"
          >
            <LogOut className="h-3.5 w-3.5" aria-hidden="true" />
            Sign out
          </button>
        </>
      ) : null}
      <p className="text-xs text-slate-400 dark:text-slate-500">Environment: {environment}</p>
    </div>
  );
}

function Shell() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const isSignIn = pathname === '/sign-in';

  if (isSignIn) {
    return (
      <div className="flex h-full flex-col">
        <SimulatorBanner />
        <main id="main" className="min-h-0 flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <a href="#main" className="skip-link bg-sky-600 px-4 py-2 text-sm text-white">
        Skip to main content
      </a>
      <SimulatorBanner />
      <SendingDisabledNotice />
      <div className="flex min-h-0 flex-1">
        <Sidebar />
        <main id="main" className="min-w-0 flex-1 overflow-y-auto p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export const rootRoute = createRootRoute({
  component: function RootComponent() {
    return (
      <Suspense fallback={<LoadingState label="Starting" />}>
        <RuntimeConfigProvider>
          <SessionProvider>
            <Shell />
          </SessionProvider>
        </RuntimeConfigProvider>
      </Suspense>
    );
  },
});
